import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_id');
    const userStr = localStorage.getItem('user');
    
    // Only add tenant ID header for tenant users (not system admins)
    let isSystemAdmin = false;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        isSystemAdmin = user.is_system_admin === true;
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Only add tenant ID header for tenant users
    if (tenantId && !isSystemAdmin) {
      config.headers['X-Tenant-ID'] = tenantId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // The MCP Connector consent page has its own inline sign-in; bouncing to
      // /login would drop the pending connection request (?req=...).
      const onConsentPage = window.location.pathname.startsWith('/mcp/authorize');
      if (!onConsentPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenant_id');
        localStorage.removeItem('tenant_subdomain');
        localStorage.removeItem('tenant_config');
        localStorage.removeItem('redirect_path');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Profit & Loss
// ---------------------------------------------------------------------------
export const profitLossApi = {
  // Income config
  getIncomeConfig: (workOrderId) => api.get(`/profit-loss/income/work-order/${workOrderId}`),
  upsertIncomeConfig: (workOrderId, data) => api.post(`/profit-loss/income/work-order/${workOrderId}`, data),

  // Expense categories
  getExpenseCategories: () => api.get('/profit-loss/expense-categories'),
  createExpenseCategory: (data) => api.post('/profit-loss/expense-categories', data),
  updateExpenseCategory: (id, data) => api.put(`/profit-loss/expense-categories/${id}`, data),
  deleteExpenseCategory: (id) => api.delete(`/profit-loss/expense-categories/${id}`),

  // Fixed expenses
  getFixedExpenses: (params) => api.get('/profit-loss/fixed-expenses', { params }),
  createFixedExpense: (data) => api.post('/profit-loss/fixed-expenses', data),
  updateFixedExpense: (id, data) => api.put(`/profit-loss/fixed-expenses/${id}`, data),
  deleteFixedExpense: (id) => api.delete(`/profit-loss/fixed-expenses/${id}`),
  bulkUpsertFixedExpenses: (data) => api.post('/profit-loss/fixed-expenses/bulk', data),

  // P&L report
  getReport: (params) => api.get('/profit-loss/report', { params }),
};

// ---------------------------------------------------------------------------
// Work Calendar
// ---------------------------------------------------------------------------
export const calendarApi = {
  getCalendar: (year, month) => api.get('/shifts/calendar', { params: { year, month } }),
  getWorkingDays: (year, month) => api.get('/shifts/calendar/working-days', { params: { year, month } }),
  getAttendanceSummary: (year, month) => api.get('/attendance/calendar-summary', { params: { year, month } }),
  setOverride: (dateStr, data) => api.put(`/shifts/calendar/${dateStr}`, data),
  removeOverride: (dateStr) => api.delete(`/shifts/calendar/${dateStr}/override`),
  generate: (data) => api.post('/shifts/calendar/generate', data),
  getWorkerDayStatuses: (dateStr) => api.get('/attendance/day-worker-statuses', { params: { date: dateStr } }),
};

// ---------------------------------------------------------------------------
// Report Settings (per-tenant tunables edited inline from report pages)
// ---------------------------------------------------------------------------
export const reportSettingsApi = {
  get: () => api.get('/tenant/settings/report-config'),
  update: (report_settings) => api.put('/tenant/settings/report-config', { report_settings }),
};

// ---------------------------------------------------------------------------
// Report Drill-Down (click a report number -> underlying fact-table rows)
// ---------------------------------------------------------------------------
export const reportsApi = {
  drillWorkLog: (params) => api.get('/reports/drill-down/work-log', { params }),
  drillAttendance: (params) => api.get('/reports/drill-down/attendance', { params }),
  drillOvertime: (params) => api.get('/reports/drill-down/overtime', { params }),
  // Inventory-sourced drills reuse the existing movement list endpoint directly.
  drillInventory: (params) => api.get('/inventory', { params }),
};

// ---------------------------------------------------------------------------
// Report Templates
// ---------------------------------------------------------------------------
export const reportTemplatesApi = {
  list: (params) => api.get('/report-templates', { params }),
  get: (id) => api.get(`/report-templates/${id}`),
  create: (data) => api.post('/report-templates', data),
  update: (id, data) => api.put(`/report-templates/${id}`, data),
  delete: (id) => api.delete(`/report-templates/${id}`),
  duplicate: (id) => api.post(`/report-templates/${id}/duplicate`),
  getDefault: (templateType) => api.get(`/report-templates/default/${templateType}`),
  getTypes: () => api.get('/report-templates/types'),
  getVariables: (templateType) => api.get(`/report-templates/variables/${templateType}`),
  render: (id, recordIds, { copies, collate, operation_ids } = {}) => api.post(`/report-templates/${id}/render`, { record_ids: recordIds, copies, collate, operation_ids }),
  preview: (data) => api.post('/report-templates/preview', data),
  getSampleRecords: (templateType) => api.get(`/report-templates/sample-records/${templateType}`),
};

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------
export const rolesApi = {
  list: () => api.get('/roles'),
  get: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  clone: (id, data) => api.post(`/roles/${id}/clone`, data),
  workerCategories: () => api.get('/roles/worker-categories'),
};

// ---------------------------------------------------------------------------
// Approval Routes
// ---------------------------------------------------------------------------
export const approvalRoutesApi = {
  list: () => api.get('/approval-routes'),
  get: (id) => api.get(`/approval-routes/${id}`),
  create: (data) => api.post('/approval-routes', data),
  update: (id, data) => api.put(`/approval-routes/${id}`, data),
  delete: (id) => api.delete(`/approval-routes/${id}`),
  getTriggerKeys: () => api.get('/approval-routes/trigger-keys'),
};

// ---------------------------------------------------------------------------
// Payroll Rules
// ---------------------------------------------------------------------------

export const payrollRulesApi = {
  list: (includeInactive) => api.get('/payroll-rules', { params: includeInactive ? { include_inactive: 'true' } : {} }),
  get: (id) => api.get(`/payroll-rules/${id}`),
  create: (data) => api.post('/payroll-rules', data),
  update: (id, data) => api.put(`/payroll-rules/${id}`, data),
  duplicate: (id, data) => api.post(`/payroll-rules/${id}/duplicate`, data),
  delete: (id) => api.delete(`/payroll-rules/${id}`),
};

// ---------------------------------------------------------------------------
// Automation Rules
// ---------------------------------------------------------------------------

export const automationRulesApi = {
  list: () => api.get('/automation'),
  get: (id) => api.get(`/automation/${id}`),
  create: (data) => api.post('/automation', data),
  update: (id, data) => api.put(`/automation/${id}`, data),
  delete: (id) => api.delete(`/automation/${id}`),
  getOptions: () => api.get('/automation/options'),
  getRuns: (id) => api.get(`/automation/${id}/runs`),
  getSampleRecords: (targetModel) => api.get(`/automation/sample-records/${targetModel}`),
  testExpression: (data) => api.post('/automation/test-expression', data),
};

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------
export const approvalsApi = {
  list: (params) => api.get('/approvals', { params }),
  get: (id) => api.get(`/approvals/${id}`),
  approve: (id, data) => api.post(`/approvals/${id}/approve`, data),
  reject: (id, data) => api.post(`/approvals/${id}/reject`, data),
  withdraw: (id, data) => api.post(`/approvals/${id}/withdraw`, data),
  bulkApprove: (data) => api.post('/approvals/bulk-approve', data),
  bulkReject: (data) => api.post('/approvals/bulk-reject', data),
  pendingCount: () => api.get('/approvals/pending-count'),
};

// ---------------------------------------------------------------------------
// API Keys
// ---------------------------------------------------------------------------
export const apiKeysApi = {
  list: () => api.get('/api-keys'),
  create: (data) => api.post('/api-keys', data),
  update: (id, data) => api.put(`/api-keys/${id}`, data),
  revoke: (id) => api.patch(`/api-keys/${id}`, { is_active: false }),
};

// ---------------------------------------------------------------------------
// MCP connector
// ---------------------------------------------------------------------------
export const mcpApi = {
  consentPreview: (req) => api.post('/mcp/consent/preview', { req }),
  consent: (req, approve, scopes) => api.post('/mcp/consent', { req, approve, ...(scopes ? { scopes } : {}) }),
  listGrants: (params) => api.get('/mcp/grants', { params }),
  revokeGrant: (id) => api.delete(`/mcp/grants/${id}`),
  getSettings: () => api.get('/mcp/settings'),
  updateSettings: (data) => api.put('/mcp/settings', data),
};

export default api;
